import { CSSProperties, useRef, useState } from 'react'

import { createStyles } from 'antd-style'

import { calculateZoomLevel } from '../../utils/dom'

import { TableResizerProps } from './interface'
import { useResizerDrag } from './useResizerDrag'
import { UseResizerDragCallbackParameter } from './useResizerDrag/interface'
import { useTableInteraction } from './useTableInteraction'
import { UseTableInteractionCallbackParameter } from './useTableInteraction/interface'

const useStyles = createStyles(({ token }) => ({
  bar: {
    position: 'fixed',
    zIndex: token.zIndexPopupBase,
    userSelect: 'none'
  }
}))

const isHeightChanging = (direction?: string) => {
  return direction === 'bottom'
}

/**
 * 计算新的行高
 */
const calcRowHeight = ({
  cellElement,
  heightChange
}: {
  cellElement: HTMLTableCellElement
  heightChange: number
}) => {
  const rowElement = cellElement.closest('tr')
  if (!rowElement) {
    throw new Error('TableResizer: Row element not found')
  }
  const currentHeight = rowElement.clientHeight
  return currentHeight + heightChange
}

/**
 * 计算新的列宽
 */
const calcColWidth = ({
  cellElement,
  widthChange
}: {
  cellElement: HTMLTableCellElement
  widthChange: number
}) => {
  return cellElement.clientWidth + widthChange
}

export const TableResizer = ({
  rootElement,
  isMatchedTarget,
  dragbarWidth = 4,
  dragzoneWidth = 10,
  onColWidthChange,
  onRowHeightChange
}: TableResizerProps) => {
  const rightHolderRef = useRef<HTMLDivElement>(null)
  const bottomHolderRef = useRef<HTMLDivElement>(null)
  const isDragingRef = useRef(false)
  const tableInteractionStateRef =
    useRef<UseTableInteractionCallbackParameter>(null)

  const [resizerStyles, setResizerStyles] =
    useState<Record<string, CSSProperties>>()

  // 初始化 resizer 的样式为 cell 所在的位置
  const initResizerStyles = () => {
    const cellElement = tableInteractionStateRef.current?.cellElement
    if (!cellElement) {
      setResizerStyles(undefined)
      return
    }
    const cellRect = cellElement.getBoundingClientRect()
    const styles: Record<string, CSSProperties> = {
      bottom: {
        backgroundColor: '',
        cursor: 'row-resize',
        height: `${dragzoneWidth}px`,
        left: `${window.scrollX + cellRect.left}px`,
        top: `${window.scrollY + cellRect.top + cellRect.height - dragzoneWidth / 2}px`,
        width: `${cellRect.width}px`
      },
      right: {
        backgroundColor: '',
        cursor: 'col-resize',
        height: `${cellRect.height}px`,
        left: `${window.scrollX + cellRect.left + cellRect.width - dragzoneWidth / 2}px`,
        top: `${window.scrollY + cellRect.top}px`,
        width: `${dragzoneWidth}px`
      }
    }
    setResizerStyles(styles)
  }

  // resizer 拖动时更新位置和样式
  const updateResizerStyles = ({
    mouseCurrentPosition,
    dragDirection
  }: UseResizerDragCallbackParameter) => {
    const tableElement = tableInteractionStateRef.current?.tableElement
    if (!tableElement) {
      return
    }
    if (isDragingRef.current && dragDirection) {
      const zoom = calculateZoomLevel(tableElement)
      const tableRect = tableElement.getBoundingClientRect()
      const styles = {
        [dragDirection]: {
          ...resizerStyles?.[dragDirection]
        }
      }
      if (isHeightChanging(dragDirection)) {
        styles[dragDirection].left = `${window.scrollX + tableRect.left}px`
        styles[dragDirection].top = `${
          window.scrollY + (mouseCurrentPosition.y - dragbarWidth / 2) / zoom
        }px`
        styles[dragDirection].height = `${dragbarWidth}px`
        styles[dragDirection].width = `${tableRect.width}px`
      } else {
        styles[dragDirection].top = `${window.scrollY + tableRect.top}px`
        styles[dragDirection].left = `${
          window.scrollX + (mouseCurrentPosition.x - dragbarWidth / 2) / zoom
        }px`
        styles[dragDirection].width = `${dragbarWidth}px`
        styles[dragDirection].height = `${tableRect.height}px`
      }
      styles[dragDirection].backgroundColor = '#adf'
      setResizerStyles(styles)
    } else {
      initResizerStyles()
    }
  }

  // 拖拽结束后更新单元格的大小
  const updateCellSize = ({
    mouseStartPosition,
    mouseCurrentPosition,
    dragDirection
  }: UseResizerDragCallbackParameter) => {
    if (!tableInteractionStateRef.current) {
      return
    }
    const { cellElement, rowIndex, colIndex } = tableInteractionStateRef.current
    if (!cellElement) {
      return
    }
    const zoom = calculateZoomLevel(cellElement)
    if (isHeightChanging(dragDirection)) {
      const heightChange =
        (mouseCurrentPosition.y - mouseStartPosition.y) / zoom
      const newHeight = calcRowHeight({
        cellElement,
        heightChange
      })
      onRowHeightChange?.({
        cellElement,
        index: rowIndex,
        value: newHeight,
        dragDelta: heightChange
      })
    } else {
      const widthChange = (mouseCurrentPosition.x - mouseStartPosition.x) / zoom
      const newWidth = calcColWidth({
        cellElement,
        widthChange
      })
      onColWidthChange?.({
        cellElement,
        index: colIndex,
        value: newWidth,
        dragDelta: widthChange
      })
    }
  }

  // 获取当前的目标节点
  useTableInteraction({
    rootElement,
    isMatchedTarget,
    subscribe: state => {
      // 正在拖拽时不更新
      if (!isDragingRef.current) {
        tableInteractionStateRef.current = state
        initResizerStyles()
      }
    }
  })

  // 拖拽交互
  useResizerDrag({
    rootElement,
    rightDragbar: rightHolderRef,
    bottomDragbar: bottomHolderRef,
    onDragStart(parameter) {
      isDragingRef.current = true
      updateResizerStyles(parameter)
    },
    onDraging(parameter) {
      isDragingRef.current = true
      updateResizerStyles(parameter)
    },
    onDragEnd(parameter) {
      isDragingRef.current = false
      updateResizerStyles(parameter)
      updateCellSize(parameter)
    }
  })

  const { cx, styles } = useStyles()

  return resizerStyles ? (
    <>
      {!!onColWidthChange && (
        <div
          ref={rightHolderRef}
          className={cx(styles.bar, 'table-resizer-right-bar')}
          role='button'
          style={resizerStyles?.right}
          tabIndex={0}
        />
      )}
      {!!onRowHeightChange && (
        <div
          ref={bottomHolderRef}
          className={cx(styles.bar, 'table-resizer-bottom-bar')}
          role='button'
          style={resizerStyles?.bottom}
          tabIndex={0}
        />
      )}
    </>
  ) : null
}
